import { useCallback } from "react";

import type { OptionPmsItemCreateInput } from "@/lib/gql/__generated__/graphql";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import * as Repository from "../../../_repository";

import type * as Type from "../type";

type Props = {
	hotelID: string;
	viewModel: Type.ViewModel;
};

export const useCreateOptionPmsItem = ({
	hotelID,
	viewModel: { amount, comment, date, guestName, guestTel, optionID, subOptionID },
}: Props) => {
	/**
	 * オプション作成
	 */
	const { onCreateOptionPmsItem } = Repository.Mutation.UseCase.useOptionPmsItemCreateMutation();

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const onCreateOptionPms = useCallback(
		async (successCallback: () => void) => {
			/**
			 * 例外ケース（Typescript の型チェックを通すため）
			 */
			if (!amount.value || !guestName.value || !optionID.value) {
				notifyUserEventInfo({
					variant: "error",
					title: "必須項目を入力してください",
					description: "お客様のお名前、オプション、数量は必須項目です。",
				});
				return;
			}

			showLoadingScreenCallback();

			const input: OptionPmsItemCreateInput = {
				amount: amount.value,
				comment: comment?.value,
				date: date,
				guestName: guestName.value,
				guestTel: guestTel?.value,
				hotelID,
				optionID: optionID.value,
				subOptionID: subOptionID?.value,
			} satisfies OptionPmsItemCreateInput;

			await onCreateOptionPmsItem(input)
				.then(({ errors }) => {
					if (!errors) {
						notifyUserEventInfo({
							variant: "default",
							title: "オプションを作成しました",
						});
						successCallback();
					}
				})
				.finally(() => hideLoadingScreenCallback());
		},
		[
			amount.value,
			comment,
			date,
			guestName.value,
			guestTel?.value,
			hideLoadingScreenCallback,
			hotelID,
			onCreateOptionPmsItem,
			optionID.value,
			showLoadingScreenCallback,
			subOptionID?.value,
		],
	);

	return {
		// オプション作成
		onCreateOptionPms,
	};
};
